import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { LoadingPopup } from 'components/common';
import {
  DatePickerField,
  InputField,
  SelectInput,
  TimerPickerField,
} from 'components/form-control';
import Select, { GetOptionsResponse } from 'components/form-control/Select';
import yup from 'config/yup.custom';
import {
  NotifyService,
  convertDobToAge,
  convertTimeToUTC,
  mergeDateTime,
} from 'helpers';
import { useDateTime } from 'hooks';
import {
  ADDRESS_DETAIL_PARENT_DETAIL_TYPE,
  ADDRESS_PARENT_DETAIL_TYPE,
  CHILDREN_PARENT_DETAIL_TYPE,
  GENERATE_PAST_SR_AND_JOB_PAYLOAD,
  GET_PARENTS_PARAMS_REQUEST,
  PARENT_TYPE,
  REQUESTS_DETAIL_TYPE,
} from 'models';
import {
  JOB_AC_PROPS,
  SITTER_AC_PARAMS_REQUEST,
  SITTER_AC_TYPE,
  SUBSCRIPTION_DETAIL_TYPE,
} from 'models/subscription';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  createRequestACAndGenPastJobApi,
  getParentAddressApi,
  getParentChildrenApi,
  getParentListApi,
} from 'services';
import { getListAcAcceptedSitterApi } from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSubscriptionDetailSuccess } from 'store/reducers/subscription';
import { theme } from 'theme';
import PopupEditAddress from '../../PopupEditAddress';
import PopupConfirmGenerateJob from '../PopupConfirmGenerateJob';
import {
  IFormGenerateJob,
  OPTIONS_DATA_TYPE,
  defaultValuesGenerateJob,
} from './data';
import { getACPurchaseHistoryAction } from 'store/reducers/subscription/actionType';

interface PopupGenerateJobProps {
  open: boolean;
  dataCreateAC?: JOB_AC_PROPS;
  handleClosePopup?: () => void;
  onSuccess?: () => void;
  isGenerateJob: boolean;
}

const PopupGenerateJobs = (props: PopupGenerateJobProps) => {
  const { open, handleClosePopup, onSuccess, dataCreateAC } = props;

  const { subscription_detail, purchaseHistoryList } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );

  const dispatch = useAppDispatch();
  const { moment } = useDateTime();

  const [requests, setRequests] = useState<REQUESTS_DETAIL_TYPE>();

  const [openEditAddress, setOpenEditAddress] = useState<boolean>(false);
  const [openAddAddress, setOpenAddAddress] = useState<boolean>(false);
  const [loadingPopup, setLoadingPopup] = useState<boolean>(false);
  const [openConfirmCreateJob, setOpenConfirmCreateJob] =
    useState<boolean>(false);

  const schema = useMemo(() => {
    return yup.object({
      parent: yup.mixed().required('Parent is required'),
      type: yup.mixed().required('Type is required'),
      from_time: yup.string().required('From time is required'),
      from_date: yup.string().required('From date is required'),
      to_time: yup.string().required('To time is required'),
      to_date: yup.string().required('To date is required'),
      children: yup.mixed().notRequired(),
      address: yup.mixed().required('Address is required'),
      sitters: yup.mixed().required('Sitter is required'),
      parent_rate: yup
        .number()
        .typeError('Parent rate must be a number')
        .integer('Parent rate must be an integer')
        .min(1, 'Parent rate must be greater than 0')
        .required('Parent rate is required'),
      sitter_rate: yup
        .number()
        .typeError('Sitter rate must be a number')
        .integer('Sitter rate must be an integer')
        .min(1, 'Sitter rate must be greater than 0')
        .required('Sitter rate is required'),
    });
  }, []);

  const form = useForm<IFormGenerateJob>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValuesGenerateJob,
  });

  const { reset, handleSubmit, control, watch, setValue } = form;

  const {
    fields: children,
    remove: removeChildren,
    append: appendChildren,
  } = useFieldArray({
    control,
    name: 'children',
  });

  const _handleClosePopup = () => {
    reset({ ...defaultValuesGenerateJob });
    setRequests(undefined);
    handleClosePopup?.();
  };

  const getAddressLabel = (data: {
    block_number: string;
    street: string;
    unit_number: string;
    postal_code: string;
  }) => {
    const { block_number, postal_code, street, unit_number } = data;
    return `${unit_number}, ${block_number} ${street}, Singapore ${postal_code}`;
  };

  const onSuccessAddOrEditAddress = (
    data?: ADDRESS_DETAIL_PARENT_DETAIL_TYPE
  ) => {
    const address = form.getValues('address');
    if (address && address.id === data?.id) {
      form.setValue('address', { ...data, label: getAddressLabel(data) });
    }
  };

  useEffect(() => {
    reset({});
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && dataCreateAC?.baseRequest?.parent) {
      dispatch(
        getACPurchaseHistoryAction({
          parent_id: dataCreateAC.baseRequest.parent.User.id,
          page: 1,
          limit: 50,
        })
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dataCreateAC?.baseRequest?.parent?.User.id]);

  useEffect(() => {
    if (dataCreateAC) {
      const setFormData = async () => {
        const startDays = moment(
          dataCreateAC.baseRequest?.start_datetime
        ).toISOString();
        const endDays = moment(
          dataCreateAC.baseRequest?.end_datetime
        ).toISOString();

        reset({
          from_time: startDays,
          from_date: startDays,
          to_time: endDays,
          to_date: endDays,
          address: dataCreateAC.baseRequest?.address
            ? {
                ...dataCreateAC.baseRequest?.address,
                label: getAddressLabel(dataCreateAC.baseRequest?.address),
              }
            : undefined,
          type: 'concierge',
          parent: dataCreateAC.baseRequest?.parent
            ? {
                id: dataCreateAC.baseRequest?.parent.User.id,
                label: `${dataCreateAC.baseRequest?.parent.User.first_name} ${dataCreateAC.baseRequest?.parent.User.last_name}`,
              }
            : undefined,
          children: [],
          sitters: undefined,
          parent_rate: purchaseHistoryList?.records?.[0]?.parent_rate || 25,
          sitter_rate: purchaseHistoryList?.records?.[0]?.sitter_rate || 20,
        });
      };
      setFormData();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreateAC, purchaseHistoryList?.records]);

  const handleGenerateJobs = async () => {
    const data = form.getValues();
    const payload: GENERATE_PAST_SR_AND_JOB_PAYLOAD = {
      parent_id: data.parent?.id || '',
      start_datetime:
        convertTimeToUTC(mergeDateTime(data.from_date, data.from_time)) || '',
      end_datetime:
        convertTimeToUTC(mergeDateTime(data.from_date, data.to_time)) || '',
      parent_rate: data.parent_rate || 25,
      sitter_rate: data.sitter_rate || 20,
      address: {
        id: data.address?.id || '',
      },
      sitter_id: data.sitters?.id || '',
      children: data.children
        .filter((it) => it.is_checked)
        ?.map((item) => ({
          id: item.id,
          picture: item.picture,
          age_range: item.age_range,
          full_name: item.full_name,
          date_of_birth: item.date_of_birth as string,
        })),
    };

    setLoadingPopup(true);
    await createRequestACAndGenPastJobApi(payload)
      .then((r) => {
        dispatch(
          setSubscriptionDetailSuccess({
            ...(subscription_detail as SUBSCRIPTION_DETAIL_TYPE),
            is_trial: false,
          })
        );
        onSuccess?.();
        NotifyService.success(r);
        setOpenConfirmCreateJob(false);
        _handleClosePopup();
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setLoadingPopup(false);
      });
  };

  const onSubmit = async (data: IFormGenerateJob) => {
    handleGenerateJobs();
  };

  const parent = watch('parent');

  const getOptions =
    (key: keyof IFormGenerateJob) =>
    async (page: number = 1, limit: number = 20, keyword?: string) => {
      let data: GetOptionsResponse | null = null;

      switch (key) {
        case 'parent':
          const paramsParent: GET_PARENTS_PARAMS_REQUEST = {
            page: page,
            limit: limit,
          };
          if (keyword) paramsParent.keyword = keyword;
          data = await getParentListApi(paramsParent)
            .then(({ data }) => {
              return {
                metadata: data.data.metadata,
                options: data.data.records.map((it: PARENT_TYPE) => ({
                  id: it.parent_id,
                  label: `${it.first_name} ${it.last_name}`,
                })),
              };
            })
            .catch((e) => {
              NotifyService.error(e);
              return null;
            });
          break;
        case 'address':
          if (parent?.id) {
            data = await getParentAddressApi(parent.id)
              .then(({ data }) => {
                return {
                  metadata: data.metadata,
                  options: data.records
                    .map((it: ADDRESS_PARENT_DETAIL_TYPE) => ({
                      id: it.id,
                      name: it.address_name,
                      street: it.street_name,
                      block_number: it.block_no,
                      unit_number: it.unit_no,
                      country: it.country,
                      postal_code: it.postal_code,
                    }))
                    .map((it: ADDRESS_DETAIL_PARENT_DETAIL_TYPE) => ({
                      ...it,
                      label: getAddressLabel(it),
                    })),
                };
              })
              .catch((e) => {
                NotifyService.error(e);
                return null;
              });
          }
          break;
        case 'sitters':
          const paramsSitters: SITTER_AC_PARAMS_REQUEST = {
            id: dataCreateAC?.baseRequest?.id,
            page: page,
            limit: limit,
          };
          if (keyword) paramsSitters.search = keyword;
          data = await getListAcAcceptedSitterApi(paramsSitters)
            .then(({ data }) => {
              return {
                metadata: data.data.metadata,
                options: data.data.records?.map((it: SITTER_AC_TYPE) => ({
                  id: it.sitter_id,
                  label: `${it.User.first_name} ${it.User.last_name}`,
                })),
              };
            })
            .catch((e) => {
              NotifyService.error(e);
              return null;
            });
          break;
      }

      return data;
    };

  useEffect(() => {
    if (parent) {
      const getChildren = async () => {
        const dataChildren: CHILDREN_PARENT_DETAIL_TYPE[] =
          await getParentChildrenApi(parent.id)
            .then(({ data }) => data.records)
            .catch((e) => {
              NotifyService.error(e);
              return [];
            });

        if (dataChildren.length) {
          removeChildren();
          appendChildren(
            dataChildren.map((item) => ({
              id: item.id,
              picture: item.picture,
              age_range: item.age_range,
              full_name: item.children_name,
              date_of_birth: item.date_of_birth,
              is_checked: !!dataCreateAC?.baseRequest?.children.find(
                (temp) => temp.id === item.id
              ),
            }))
          );
        }
      };
      getChildren();
    } else {
      removeChildren();
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent]);

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 523,
          borderRadius: 0,
          height: 'auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: '20px 25px',
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            Generate Past Jobs
          </Typography>
          <IconButton
            onClick={handleClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
      </DialogTitle>
      {loadingPopup && <LoadingPopup color="primary" />}
      <DialogContent
        sx={{
          p: '30px 24px 50px 26px',
        }}
        dividers
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
          <Stack height="100%" justifyContent="space-between">
            <Stack spacing="16px">
              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-start">
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <Select
                      isDisabled={true}
                      title="Parent"
                      name="parent"
                      form={form}
                      isHasMore
                      filterFunc={false}
                      getOptions={getOptions('parent')}
                      onChange={() => {
                        setValue('address', undefined);
                        setValue('sitters', undefined);
                      }}
                    />
                  </Stack>
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <SelectInput
                      isDisabled={true}
                      title="Request Type"
                      form={form}
                      options={OPTIONS_DATA_TYPE}
                      name="type"
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-end">
                  <Stack
                    sx={{
                      width: '100%',
                    }}
                  >
                    <DatePickerField
                      title="Date"
                      form={form}
                      name="from_date"
                      maxDate={moment()}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-end">
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <TimerPickerField
                      title="Time"
                      form={form}
                      name="from_time"
                    />
                  </Stack>
                  <Typography height="30px" color="#545454">
                    -
                  </Typography>
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <TimerPickerField
                      form={form}
                      name="to_time"
                      minTime={moment(watch('from_time')).add(3, 'hours')}
                    />
                  </Stack>
                </Stack>
              </Grid>

              <Grid>
                <Stack direction="row" spacing="14px" alignItems="flex-end">
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <InputField
                      title="Parent Rate"
                      name="parent_rate"
                      type="number"
                      control={form.control}
                      placeholder="0"
                      inputProps={{
                        min: 1,
                        step: 1,
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        // Chỉ cho phép số và các phím điều hướng/xóa
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Stack>
                  <Stack
                    sx={{
                      width: '50%',
                    }}
                  >
                    <InputField
                      title="Sitter Rate"
                      name="sitter_rate"
                      type="number"
                      control={form.control}
                      placeholder="0"
                      inputProps={{
                        min: 1,
                        step: 1,
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        // Chỉ cho phép số và các phím điều hướng/xóa
                        if (
                          !/[0-9]/.test(e.key) &&
                          ![
                            'Backspace',
                            'Delete',
                            'ArrowLeft',
                            'ArrowRight',
                            'Tab',
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>

              <Grid>
                <Select
                  title="Address"
                  name="address"
                  form={form}
                  isHasMore
                  filterFunc
                  refreshWhenOpen
                  getOptions={getOptions('address')}
                />
              </Grid>
              <Grid>
                <Select
                  title="Sitters"
                  name="sitters"
                  form={form}
                  isHasMore
                  filterFunc
                  refreshWhenOpen
                  getOptions={getOptions('sitters')}
                />
              </Grid>
              <Grid>
                <InputLabel
                  sx={{
                    fontWeight: 500,
                    fontSize: '15px',
                    lineHeight: '100%',
                    color: '#000',
                    marginBottom: '10px',
                  }}
                >
                  Child
                </InputLabel>
                <FormGroup>
                  <Stack spacing="17px">
                    {children?.map((item, index) => {
                      const child = form.watch(`children.${index}`);
                      return (
                        <Stack
                          key={item.id}
                          direction="row"
                          alignItems="center"
                        >
                          <Controller
                            name={`children.${index}.is_checked`}
                            control={form.control}
                            render={({ field }) => (
                              <Checkbox
                                {...field}
                                checked={field.value ?? false}
                                sx={{
                                  '&.Mui-checked': {
                                    color: theme.palette.secondary.main,
                                  },
                                }}
                              />
                            )}
                          />
                          <Avatar
                            alt={child.full_name}
                            src={child.picture ?? ''}
                          />
                          <Stack ml="8px">
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '13px',
                                lineHeight: '20px',
                                letterSpacing: '0.1px',
                              }}
                            >
                              {child.full_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                lineHeight: '20px',
                                letterSpacing: '0.1px',
                              }}
                            >
                              {convertDobToAge(child.date_of_birth ?? '')}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </FormGroup>
              </Grid>
            </Stack>

            <Stack
              spacing="8px"
              direction="row"
              justifyContent="space-between"
              mt="32px"
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                onClick={() => {
                  reset();
                  _handleClosePopup();
                }}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                sx={{
                  width: '233.5px',
                  height: '30px',
                }}
                type="submit"
              >
                Generate
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>

      {requests && (openEditAddress || openAddAddress) && (
        <PopupEditAddress
          open
          isEdit={openEditAddress}
          subject={requests?.parent?.parent_id}
          idAddress={form.watch('address')?.id}
          handleClosePopup={() => {
            setOpenAddAddress(false);
            setOpenEditAddress(false);
          }}
          onSuccess={onSuccessAddOrEditAddress}
        />
      )}
      <PopupConfirmGenerateJob
        open={openConfirmCreateJob}
        onClosePopup={() => setOpenConfirmCreateJob(false)}
        onConfirmGen={() => {
          setLoadingPopup(false);
          handleGenerateJobs();
        }}
      />
    </Dialog>
  );
};

export default PopupGenerateJobs;
